<template>
  <li class="mb-4">
    <div :class="['relative p-6 border rounded', status.container]">
      <h3 class="flex items-center mb-2">
        <icon
          v-if="status.icon"
          :icon="status.icon"
          class="w-6 h-6 mr-2"
          :class="status.icon_class"
        />
        {{ status.status }}
      </h3>
      <p>
        <b>{{ requestData.option.category }}:</b>
        {{ requestData.option.request_type }}
      </p>
      <template v-if="isBankTemplate">
        <dl class="space-y-2 mt-2">
          <div
            v-for="item in formBankDetailsTemplate"
            :key="item.key"
            class="flex"
          >
            <dt class="font-bold mr-2">
              {{ item.label }}:
            </dt>
            <dd class="italic">
              {{ requestData.request_data[item.key] || "-" }}
            </dd>
          </div>
        </dl>
      </template>
      <template v-else-if="isStaffingItem">
        <dl class="space-y-2 mt-2 border-t border-grey-800 pt-2">
          <div
            v-for="item in formStaffTemplate"
            :key="item.key"
            style="display: grid; grid-template-columns: 1fr 2fr; grid-gap: 0.5rem;"
          >
            <dt class="font-bold mr-2">
              {{ item.label }}:
            </dt>
            <dd class="italic">
              {{ item.transformer(requestData.request_data[item.key]) }}
            </dd>
          </div>
        </dl>
      </template>
      <p
        v-else
        class="text-sm italic"
        v-html="requestData.request_text"
      />
      <icon-link
        v-if="requestData.option.upload_required && requestData.file.id"
        icon="cheveron-down-circle"
        :href="
          route('applications.files.download', {
            application: uuid,
            file: requestData.file.id,
            project: 1
          })
        "
        target="_blank"
      >
        Download file
      </icon-link>
      <form
        v-if="status.status === 'Pending'"
        class="p-6 mt-6 bg-white rounded-sm"
        @submit.prevent="sendResponse"
      >
        <h2>Response</h2>

        <div class="my-4">
          <label
            class="block mb-1 text-xs font-bold uppercase"
            for="action"
          >
            Action
          </label>
          <select
            id="action"
            v-model="form.action"
            name="action"
            class="p-1 border border-grey-300"
            required
          >
            <option
              :value="null"
              disabled
            >
              Select a action...
            </option>
            <option value="approve">
              Approve
            </option>
            <option value="reject">
              Reject
            </option>
          </select>
        </div>

        <div class="my-4">
          <label
            class="block mb-1 text-xs font-bold uppercase"
            for="message"
          >
            Message
          </label>
          <textarea
            id="message"
            v-model="form.message"
            name="message"
            class="block w-full p-2 border border-grey-300 min-h-80"
            required
          />
        </div>
        <div class="w-48">
          <app-button :disabled="formDisabled">
            {{ formButtonLabel }}
          </app-button>
        </div>
      </form>
      <div
        v-else
        class="pt-4 mt-4 border-t"
        style="border-color: currentColor"
        v-html="parseMarkdown(requestData.message)"
      />
    </div>
    <p
      class="mt-1 italic text-2xs text-grey-800"
      v-html="stamp"
    />
  </li>
</template>

<script>
import { format, parseISO } from "date-fns";

export default {
  name: "FundingProjectRequestItem",
  props: {
    request: {
      type: Object,
      required: true
    },
    projectUuid: {
      type: String,
      required: true
    },
    fundingPotId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      requestData: this.request,
      form: {
        loading: false,
        action: null,
        message: null
      }
    };
  },
  computed: {
    uuid() {
      return window._project;
    },
    template() {
      return this.requestData.option.template;
    },
    isTextTemplate() {
      return this.template === "text";
    },
    isBankTemplate() {
      return this.template === "bank";
    },
    isStaffingItem() {
      return this.template.startsWith("staff:");
    },
    status() {
      let status = {};
      if (!this.requestData.approved_at && !this.requestData.rejected_at) {
        (status.status = "Pending"),
          (status.container =
            "border-primary-200 bg-primary-100 text-primary-900");
        status.icon = "refresh";
        status.icon_class = "icons-primary";
      } else if (this.requestData.approved_at) {
        (status.status = "Approved"),
          (status.container =
            "border-positive-200 bg-positive-100 text-positive-900");
        status.icon = "check";
        status.icon_class = "icons-positive";
      } else if (this.requestData.rejected_at) {
        (status.status = "Change request rejected"),
          (status.container =
            "border-negative-200 bg-negative-100 text-negative-900");
        status.icon = "close-circle";
        status.icon_class = "icons-negative";
      }

      return status;
    },
    date() {
      return format(
        parseISO(this.requestData.created_at),
        "do MMMM yyyy HH:mm"
      );
    },
    stamp() {
      return `<b>${this.requestData.created_by_user.name} / </b> ${this.date}`;
    },
    formDisabled() {
      return !this.form.action || this.form.loading;
    },
    formButtonLabel() {
      return this.form.action?.toUpperCase() ?? "Status required";
    },
    requestTextObject() {
      try {
        return JSON.parse(this.requestData.request_text);
      } catch (e) {
        return {};
      }
    },
    formStaffTemplate() {
      return [
        {
          label: "Name",
          key: "name",
          transformer: (value) => value || "-",
        },
        {
          label: "Job Title",
          key: "job_title",
          transformer: (value) => value || "-",
        },
        {
          label: "Salary",
          key: "salary",
          transformer: (value) => (value ? `£${value}` : "-"),
        },
        {
          label: "Day Rate",
          key: "day_rate",
          transformer: (value) => (value ? `£${value}` : "-"),
        },
        {
          label: "Employer NIC",
          key: "employer_nic",
          transformer: (value) => (value ? `£${value}` : "-"),
        },
        {
          label: "Employer Pension",
          key: "employer_pension",
          transformer: (value) => (value ? `£${value}` : "-"),
        },
        {
          label: "Medical / Health Premiums",
          key: "medical_health_premiums",
          transformer: (value) => (value ? `£${value}` : "-"),
        },
        {
          label: "Life Assurance Premiums",
          key: "life_assurance_premiums",
          transformer: (value) => (value ? `£${value}` : "-"),
        },
        {
          label: "Message",
          key: "message",
          transformer: (value) => value || "-",
        }
      ];
    },
    formBankDetailsTemplate() {
      return [
        {
          label: "Bank Name",
          key: "bank_name"
        },
        {
          label: "Bank Address",
          key: "bank_address"
        },
        {
          label: "Account Name",
          key: "account_name"
        },
        {
          label: "Sort Code",
          key: "sort_code"
        },
        {
          label: "Account Number",
          key: "account_number"
        },
        {
          label: "Message",
          key: "message"
        }
      ];
    }
  },
  methods: {
    sendResponse() {
      if (
        !confirm(`Are you sure you want to ${this.form.action} this request?`)
      ) {
        return;
      }
      this.form.loading = true;
      axios
        .post(
          route("funding.projects.request.update", {
            funding: this.fundingPotId,
            project: this.projectUuid,
            change_request: this.request.id
          }),
          this.form
        )
        .then(({ data }) => (this.requestData = { ...this.request, ...data }))
        .finally(() => (this.form.loading = false));
    }
  }
};
</script>
